import React, { useState, useRef, useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";

const feelingsData = [
  { label: "HAPPY", image: "/Asset91.svg" },
  { label: "SAD", image: "/anjuf.svg" },
  { label: "ANGRY", image: "/manjuf.svg" },
  { label: "AFRAID", image: "/sherf.svg" },
  { label: "DISGUSTED", image: "/robertf.svg" },
  { label: "SURPRISED", image: "/rajuf.svg" },
  { label: "EXCITED", image: "/rehmanf.svg" },
  { label: "CALM", image: "/julief.svg" },
  { label: "WRITE YOUR OWN", image: "/questionmark.svg"}
];

const FeelingsJournal = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [selectedFeeling, setSelectedFeeling] = useState("");
  const [customText, setCustomText] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  
  // Ref for the textarea to scroll to it
  const textareaRef = useRef(null);

  const handleFeelingSelect = (label) => {
    // If "WRITE YOUR OWN" is selected, show the input box
    if (label === "WRITE YOUR OWN") {
      setShowCustomInput(true);
      setSelectedFeeling(""); // Clear any previous selection
    } else {
      // For other feelings, hide input box and set selection
      setShowCustomInput(false);
      setSelectedFeeling(label);
      setCustomText(""); // Clear custom text when selecting predefined feeling
    }
    
    // Play click sound
    try {
      const audio = new Audio("/sounds/click.mp3");
      audio.play().catch(err => console.warn("Click sound failed:", err));
    } catch (err) {
      console.warn("Click sound not available:", err);
    }
  };

  // Auto-scroll to textarea when it becomes visible
  useEffect(() => {
    if (showCustomInput && textareaRef.current) {
      // Small delay to ensure the textarea is rendered
      setTimeout(() => {
        textareaRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        textareaRef.current?.focus();
      }, 100);
    }
  }, [showCustomInput]);

  const handleSubmit = async () => {
    const feelingToSubmit = selectedFeeling || customText;
    
    if (!feelingToSubmit) {
      alert("Please select a feeling or write something!");
      return;
    }

    try {
      await fetch(`https://sochu.online/api/feeling/${user.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          feeling: feelingToSubmit,
          customText: customText || null 
        }),
      });
      
      // Play success sound
      try {
        const audio = new Audio("/sounds/success.mp3");
        audio.play().catch(err => console.warn("Success sound failed:", err));
      } catch (err) {
        console.warn("Success sound not available:", err);
      }
      
      navigate("/learn");
    } catch (error) {
      console.error("Error submitting feeling:", error);
      navigate("/learn"); // Still navigate even if API fails
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Container with responsive padding and max-width */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        <div className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl flex flex-col items-center justify-center">
          
          {/* Character Icon - Much bigger sizing */}
          <div className="mb-6 sm:mb-8 md:mb-10">
            <div className="w-41 h-41 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 flex items-center justify-center">
              <img 
                src="/Sochu_logo_Feelings.svg" 
                alt="Character" 
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Title - Bigger text size */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-3 sm:mb-4 md:mb-5 text-center">
            FEELINGS JOURNAL
          </h1>

          {/* Subtitle - Bigger text and spacing */}
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 text-center mb-3 sm:mb-4 md:mb-5 px-2 sm:px-4 md:px-6 leading-tight">
            Journaling our feelings everyday helps us keep in touch with ourselves!
          </p>

          {/* Question - Bigger text */}
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-black text-center mb-6 sm:mb-8 md:mb-10">
            How are you feeling today?
          </p>

          {/* Feelings Grid - Bigger cards with 3 columns */}
          <div className="grid grid-cols-3 gap-3 sm:gap-4 md:gap-5 lg:gap-6 mb-6 sm:mb-8 md:mb-10 w-full">
            {feelingsData.map((feeling, index) => (
              <button
                key={feeling.label}
                onClick={() => handleFeelingSelect(feeling.label)}
                className={`
                  bg-white border-2 rounded-xl sm:rounded-2xl md:rounded-3xl 
                  p-3 sm:p-4 md:p-5 lg:p-6
                  flex flex-col items-center justify-center
                  transition-all duration-200 shadow-sm hover:shadow-lg 
                  min-h-[100px] sm:min-h-[110px] md:min-h-[120px] lg:min-h-[140px]
                  ${(selectedFeeling === feeling.label || (feeling.label === "WRITE YOUR OWN" && showCustomInput))
                    ? 'border-blue-500 bg-blue-50 scale-105' 
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
              >
                <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 mb-2 sm:mb-3 flex items-center justify-center">
                  <img 
                    src={feeling.image} 
                    alt={feeling.label}
                    className="w-full h-full object-contain"
                  />
                </div>
                <span className="text-xs sm:text-sm md:text-base lg:text-lg font-medium text-gray-700 text-center leading-tight">
                  {feeling.label}
                </span>
              </button>
            ))}
          </div>

          {/* Conditional Custom Text Input - Much bigger and with auto-scroll */}
          {showCustomInput && (
            <div className="w-full mb-6 sm:mb-8 md:mb-10">
              <textarea
                ref={textareaRef}
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                placeholder="Write how you're feeling today..."
                className="w-full h-28 sm:h-32 md:h-36 lg:h-40 p-4 sm:p-5 md:p-6 
                         bg-gray-50 border-2 border-gray-200 rounded-xl sm:rounded-2xl md:rounded-3xl 
                         text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 placeholder-gray-400 
                         resize-none focus:outline-none focus:border-blue-300 focus:bg-white 
                         transition-colors shadow-sm focus:shadow-md"
                autoFocus
              />
            </div>
          )}

          {/* Submit Button - Much bigger sizing */}
          <button
            onClick={handleSubmit}
            className="w-full py-4 sm:py-5 md:py-6 lg:py-8 
                     bg-green-500 text-white font-bold 
                     text-lg sm:text-xl md:text-2xl lg:text-3xl 
                     rounded-full shadow-lg hover:bg-green-600 hover:shadow-xl
                     transition-all duration-200 focus:outline-none 
                     transform hover:scale-[1.02] active:scale-[0.98]"
            style={{ backgroundColor: '#22C55E' }}
          >
            SUBMIT
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeelingsJournal;
